import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Spinner from "components/Common/Spinner";
import SvgIcon from "components/Common/SvgIcon";
import { NO_OP_FUNCTION } from "services/helpers";
import "./style.scss";

const BLOCK_NAME = "Button";

class Button extends React.Component {
  /**
   * Get classNames for Button
   * @returns {String}
   */
  getClassName() {
    const {
      icon,
      light,
      lightPrimary,
      disabled,
      size,
      round,
      text,
      clear,
      clearWithBorder,
      iconLarge,
      disabledStyleFlat,
      customClassName,
      isLoading,
      stretched,
      linkLike
    } = this.props;

    let sizeClassName = "";

    switch (size) {
      case "x-small":
        sizeClassName = "Button--x-small";
        break;
      case "small":
        sizeClassName = "Button--small";
        break;
      case "big":
        sizeClassName = "Button--big";
        break;
      default:
    }

    return classNames(
      BLOCK_NAME,
      sizeClassName,
      {
        "Button--glyph": icon,
        "Button--light": light,
        "Button--light-primary": lightPrimary,
        "Button--disabled": disabled,
        "Button--round": round,
        "Button--text": text,
        "Button--clear": clear,
        "Button--clear-with-border": clearWithBorder,
        "Button--large-icon": iconLarge,
        "Button--disabled-flat": disabledStyleFlat,
        "Button--loading": isLoading,
        "Button--stretched": stretched,
        "Button--no-text": !text,
        [`${BLOCK_NAME}--link-like`]: linkLike,
        [customClassName]: customClassName
      }
    );
  }

  /**
   * Set colour and backgroundColor styles of Button based on props
   */
  getStyle() {
    const {
      tint, light, round, small
    } = this.props;
    const styles = {};

    if (tint) {
      styles[light || (round && small) ? "color" : "backgroundColor"] = tint;
    }

    return styles;
  }

  /**
   * Conditionally render Button icon
   */
  getIcon() {
    const {
      icon,
      iconHeightOverride,
      iconColor,
      fillColor
    } = this.props;

    return (
      <SvgIcon
        icon={icon}
        height={iconHeightOverride}
        fillColor={iconColor || fillColor}
      />
    );
  }

  /**
   * Conditionally render secondary Button icon
   */
  getSecondIcon() {
    const {
      secondIcon, secondIconHeightOverride, iconColor, fillColor
    } = this.props;

    return (
      <SvgIcon
        icon={secondIcon}
        height={secondIconHeightOverride}
        fillColor={iconColor || fillColor}
        customClassName="Button--second-glyph"
      />
    );
  }

  /**
   * Conditionally displays the Spinner (loading indicator) element
   */
  getSpinner() {
    const { light, disabled, small } = this.props;

    return (
      <Spinner
        customClassName="Button__loader"
        dark={Boolean(light || disabled)}
        small={small}
      />
    );
  }

  /**
   * If showActiveBackground is true, will display button with background (cosmetic element only)
   */
  static getActiveBackground() {
    return <span className="Button__active-background" />;
  }

  handleOnClick = (e) => {
    const { onClick } = this.props;
    onClick(e);
  };

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      title,
      text,
      icon,
      secondIcon,
      disabled,
      isLoading,
      center,
      left,
      showActiveBackground,
      tabIndex,
      children,
      onMouseDown,
      type
    } = this.props;

    return (
      <button
        className={this.getClassName()}
        onClick={this.handleOnClick}
        title={title || text || ""}
        tabIndex={parseInt(tabIndex, 10)}
        disabled={disabled}
        onMouseDown={onMouseDown}

        // type string is ok
        // eslint-disable-next-line react/button-has-type
        type={type}
      >
        {icon && !isLoading && this.getIcon()}
        {showActiveBackground && Button.getActiveBackground()}
        {isLoading && this.getSpinner()}
        <span
          className={classNames(
            "Button__text", {
              "Button__text--left": left,
              "Button__text--center": center
            }
          )}
          style={this.getStyle()}
        >
          {text}
        </span>
        {secondIcon && !isLoading && this.getSecondIcon()}
        {children}
      </button>
    );
  }
}

Button.propTypes = {
  /** The SVG icon of the Button */
  icon: PropTypes.string,
  /** The height of the Button's icon (needed to overwrite default 24px value) */
  iconHeightOverride: PropTypes.number,
  /** The secondary SVG icon of the Button */
  secondIcon: PropTypes.string,
  /** The height of the Button's secondary icon (needed to overwrite default 24px value) */
  secondIconHeightOverride: PropTypes.number,
  /** The text inside the Button */
  text: PropTypes.string,
  /** The title attribute of the Button for a11y */
  title: PropTypes.string,
  /** The bright/light colour scheme */
  light: PropTypes.bool,
  /** The bright/light colour scheme, with text in color-ui-primary (blue) */
  lightPrimary: PropTypes.bool,
  /** Determined button sizing (null/default = medium size) */
  size: PropTypes.oneOf(["x-small", "small", "medium", "big"]),
  /** Round corners of Button */
  round: PropTypes.bool,
  /** The transparent Button style */
  clear: PropTypes.bool,
  /** onClick event handler */
  onClick: PropTypes.func,
  /** onMouseDown event handler */
  onMouseDown: PropTypes.func,
  /** Disables the Button */
  disabled: PropTypes.bool,
  /** Center the button text */
  center: PropTypes.bool,
  /** Left align the button text */
  left: PropTypes.bool,
  /** Make the SVG icon larger */
  iconLarge: PropTypes.bool,
  /** When true displays a loading indicator instead of icon */
  isLoading: PropTypes.bool,
  /** Will fit container */
  stretched: PropTypes.bool,
  /** Transparent Button style with border */
  clearWithBorder: PropTypes.bool,
  /** Specify custom styles to override defaults */
  customClassName: PropTypes.string,
  /** Alternative disabled Button style. Requires disabled to be true */
  disabledStyleFlat: PropTypes.bool,
  linkLike: PropTypes.bool,
  /** Set small size for spinner & */
  small: PropTypes.bool,
  /** icon fill color */
  fillColor: PropTypes.string,
  iconColor: PropTypes.string,
  tint: PropTypes.string,
  /** Whether will display button with background (cosmetic element only) */
  showActiveBackground: PropTypes.bool,
  /** Allows tabIndex customization */
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  type: PropTypes.string
};

Button.defaultProps = {
  icon: null,
  iconHeightOverride: null,
  secondIcon: null,
  secondIconHeightOverride: null,
  text: null,
  title: null,
  light: false,
  lightPrimary: false,
  size: null,
  round: false,
  clear: false,
  onClick: NO_OP_FUNCTION,
  onMouseDown: NO_OP_FUNCTION,
  disabled: false,
  center: false,
  left: false,
  iconLarge: false,
  isLoading: false,
  stretched: false,
  clearWithBorder: false,
  customClassName: null,
  disabledStyleFlat: false,
  linkLike: false,
  small: null,
  fillColor: null,
  iconColor: null,
  tint: null,
  showActiveBackground: false,
  tabIndex: 0,
  children: null,
  type: "button"
};

export default Button;
